<script setup lang="ts">
import { ref } from 'vue'

defineProps({
  balance: { type: Number, default: 1991.75 },
  goal: { type: Number, default: 2000 },
})

const countdown = ref('02:23:59:59')
const timerProgress = ref(99.53)
const isSpinning = ref(false)
const showWinHighlight = ref(false)
const rotationDegrees = ref(0)
const lastRotation = ref(0)
const wonIndex = ref(-1)

// Timer logic (DD:HH:MM:SS)
setInterval(() => {
  const parts = countdown.value.split(':').map(Number)
  if (parts.length === 4) {
    const [dd = 0, hh = 0, mm = 0, ss = 0] = parts
    let totalSeconds = dd * 86400 + hh * 3600 + mm * 60 + ss
    if (totalSeconds > 0) totalSeconds--

    const d = Math.floor(totalSeconds / 86400)
      .toString()
      .padStart(2, '0')
    const h = Math.floor((totalSeconds % 86400) / 3600)
      .toString()
      .padStart(2, '0')
    const m = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    const s = (totalSeconds % 60).toString().padStart(2, '0')
    countdown.value = `${d}:${h}:${m}:${s}`
  }
}, 1000)

const prizes = [
  { label: 'Rs 0.01~Rs 10', icon: 'icon1.webp' },
  { label: 'Rs 50~Rs 100', icon: 'icon2.webp' },
  { label: 'Rs 20~Rs 50', icon: 'icon3.webp' },
  { label: 'Rs 100~Rs 500', icon: 'icon4.webp' },
  { label: 'Rs 30~Rs 100', icon: 'icon5.webp' },
  { label: 'Rs 10~Rs 20', icon: 'bigGold.webp' },
  { label: 'Rs 50~Rs 200', icon: 'icon7.webp' },
  { label: 'Rs 100~Rs 300', icon: 'icon8.webp' },
]

const getIconPath = (name: string) => {
  return new URL(`../../assets/activity/pinduo/${name}`, import.meta.url).href
}

const spinWheel = (targetIndex: number) => {
  if (isSpinning.value) return
  isSpinning.value = true
  showWinHighlight.value = false
  wonIndex.value = targetIndex

  const rotations = 8
  const sliceAngle = 360 / 8
  const targetAngle = rotations * 360 + (8 - targetIndex) * sliceAngle

  lastRotation.value += targetAngle
  rotationDegrees.value = lastRotation.value

  setTimeout(() => {
    isSpinning.value = false
    showWinHighlight.value = true
  }, 4000)
}

const handleSpinClick = () => {
  const randomIndex = Math.floor(Math.random() * 8)
  spinWheel(randomIndex)
}

const records = [
  { name: 'Guest**49', time: '2026-03-30 19:40', reward: 'Rs 2000' },
  { name: 'Guest**12', time: '2026-03-30 19:35', reward: 'Rs 20' },
  { name: 'Guest**88', time: '2026-03-30 19:30', reward: 'Rs 50' },
  { name: 'Guest**71', time: '2026-03-30 19:22', reward: 'Rs 100' },
  { name: 'Guest**33', time: '2026-03-30 19:10', reward: 'Rs 500' },
]
</script>

<template>
  <div class="pinduo-panel">
    <!-- Full-screen Header -->
    <header class="pinduo-header">
      <div class="header-left">
        <button class="back-btn header-icon" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <path
              fill="currentColor"
              d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6l6 6l1.41-1.41z"
            />
          </svg>
        </button>
        <span class="title">Refer &amp; Win</span>
        <span class="title-badge">🎁 Special Event</span>
      </div>
      <div class="header-center">
        <!-- Countdown in header for PC -->
        <div class="header-countdown">
          <span class="hc-label">Expires in:</span>
          <div class="hc-timer">
            <span v-for="(seg, i) in countdown.split(':')" :key="i" class="time-seg">
              {{ seg }}<small v-if="i < 3">{{ ['d', 'h', 'm'][i] }}</small>
            </span>
          </div>
        </div>
      </div>
      <div class="header-right">
        <img src="@/assets/activity/pinduo/a-Rules.webp" class="header-icon-img" title="Rules" />
        <img src="@/assets/activity/pinduo/History.webp" class="header-icon-img" title="History" />
      </div>
    </header>

    <!-- Winning Ticker -->
    <div class="ticker-container">
      <div class="winning-ticker">
        <span class="hot">🔥 Hot news!</span>
        <span class="ticker-sep">|</span>
        <span class="user">Guest**49</span>
        <span class="text">just claimed</span>
        <span class="amount">Rs 2000!</span>
      </div>
    </div>

    <!-- PC Two-Column Layout -->
    <div class="pinduo-body">

      <!-- LEFT COLUMN: Wheel -->
      <div class="col-left">
        <!-- Wheel Card -->
        <div class="wheel-card">
          <section class="wheel-area">
            <div class="wheel-outer-box">
              <!-- Sparkle Background -->
              <img src="@/assets/activity/pinduo/bj_1.webp" class="character-backdrop" />

              <!-- Background glow/highlight when won -->
              <img
                v-if="showWinHighlight"
                src="@/assets/activity/pinduo/zhuanpan_liang.webp"
                class="win-light-bg"
              />

              <!-- Outer frame -->
              <img src="@/assets/activity/pinduo/zhuanpan_waiku.webp" class="wheel-border" />

              <div
                class="wheel-inner"
                :style="{ transform: `translate(-49%, -49%) rotate(${rotationDegrees}deg)` }"
              >
                <!-- Plate -->
                <img src="@/assets/activity/pinduo/pan-DKO0TqPi.webp" class="wheel-plate" />

                <!-- Prize Contents -->
                <div
                  v-for="(prize, idx) in prizes"
                  :key="idx"
                  class="prize-slot"
                  :style="{ transform: `rotate(${idx * 45}deg)` }"
                >
                  <span class="prize-text">{{ prize.label }}</span>
                  <img :src="getIconPath(prize.icon)" class="p-icon" />
                </div>
              </div>

              <!-- Pointer / Center Button -->
              <div class="spin-center" @click="handleSpinClick">
                <img src="@/assets/activity/pinduo/zhuanpan_spi.webp" class="spin-btn-img" />
                <div class="spin-text-overlay">SPIN<br />x1</div>
              </div>
            </div>
          </section>

          <div class="invite-row">
            <div class="invite-info">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Invite 1 friend to register and get 1 Spin
            </div>
            <button class="invite-btn" :class="{ spinning: isSpinning }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
                <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
                <line x1="6" y1="1" x2="6" y2="4"/>
                <line x1="10" y1="1" x2="10" y2="4"/>
                <line x1="14" y1="1" x2="14" y2="4"/>
              </svg>
              Invite 1 Friend · Get 1 FREE Spin
            </button>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Stats + Records -->
      <div class="col-right">

        <!-- Balance Card -->
        <section class="balance-card">
          <div class="bc-label">Current Balance</div>
          <div class="bc-amount">Rs 1,990.51</div>

          <div class="progress-section">
            <div class="progress-meta">
              <span class="progress-label">Progress to claim</span>
              <span class="progress-pct">{{ timerProgress }}%</span>
            </div>
            <div class="progress-bar-bg">
              <div class="progress-fill" :style="{ width: timerProgress + '%' }">
                <div class="progress-glow"></div>
              </div>
            </div>
            <div class="progress-goal">
              <span class="goal-note">🏆 Goal: <strong>Rs 2,000</strong></span>
              <span class="goal-remain">Just <strong>Rs 9.49</strong> more!</span>
            </div>
          </div>

          <button class="claim-btn" :disabled="balance < goal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Claim Reward
          </button>
        </section>

        <!-- Spin Records Card -->
        <section class="records-card">
          <div class="record-title">
            <span class="record-icon">🎡</span>
            SPIN Record
          </div>

          <div class="record-table">
            <div class="table-header">
              <span>Name</span>
              <span>Register Time</span>
              <span>Reward</span>
            </div>
            <div class="table-body">
              <div v-for="(record, idx) in records" :key="idx" class="table-row" :class="{ 'row-highlight': idx === 0 }">
                <span class="user-name">
                  <span class="user-dot"></span>
                  {{ record.name }}
                </span>
                <span class="reg-time">{{ record.time }}</span>
                <span class="reward-text">{{ record.reward }}</span>
              </div>
            </div>
          </div>

          <div class="record-footer">
            <button class="btn-load-more-rec">Load More Records</button>
          </div>
        </section>

        <!-- Invite Steps Card -->
        <section class="steps-card">
          <div class="steps-title">How It Works</div>
          <div class="steps-list">
            <div class="step-item">
              <div class="step-num">1</div>
              <div class="step-text">Share your invite link with friends</div>
            </div>
            <div class="step-connector"></div>
            <div class="step-item">
              <div class="step-num">2</div>
              <div class="step-text">Friend registers &amp; deposits</div>
            </div>
            <div class="step-connector"></div>
            <div class="step-item">
              <div class="step-num">3</div>
              <div class="step-text">You get <span class="step-hl">1 FREE Spin</span> + bonus!</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===========================
   Base Panel
   =========================== */
.pinduo-panel {
  width: 100%;
  min-height: 100%;
  background: url('@/assets/hall/background.webp') no-repeat center top;
  background-size: cover;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  flex-direction: column;
}

/* ===========================
   Header
   =========================== */
.pinduo-header {
  height: 60px;
  background: rgba(20, 20, 20, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.back-btn {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #fff;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.title {
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.title-badge {
  display: none;
  font-size: 0.75rem;
  background: linear-gradient(135deg, #ff6b35, #f7c948);
  color: #1a1a1a;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 99px;
}

.header-center {
  flex: 0 0 auto;
}

.header-countdown {
  display: none;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 6px 16px;
}

.hc-label {
  font-size: 0.75rem;
  color: #aaa;
  white-space: nowrap;
}

.hc-timer {
  display: flex;
  gap: 6px;
}

.time-seg {
  font-size: 1rem;
  font-weight: 800;
  color: var(--theme-text-highlight, #ffda6b);
  font-variant-numeric: tabular-nums;
}

.time-seg small {
  font-size: 0.65rem;
  font-weight: 500;
  color: #aaa;
  margin-left: 1px;
}

.header-right {
  display: flex;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

.header-icon-img {
  width: 30px;
  height: 30px;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.2s, transform 0.2s;
  border-radius: 6px;
}
.header-icon-img:hover {
  opacity: 1;
  transform: scale(1.1);
}

/* ===========================
   Ticker
   =========================== */
.ticker-container {
  background: linear-gradient(90deg, rgba(76, 163, 46, 0.15), rgba(255, 218, 107, 0.1), rgba(76, 163, 46, 0.15));
  border-bottom: 1px solid rgba(76, 163, 46, 0.25);
  padding: 8px 20px;
}

.winning-ticker {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: #ddd;
  overflow: hidden;
}

.winning-ticker .hot {
  color: var(--theme-news-hot, #ffda6b);
  font-weight: 700;
  white-space: nowrap;
}

.ticker-sep {
  color: #444;
}

.winning-ticker .user {
  font-weight: 700;
  color: #fff;
}

.winning-ticker .amount {
  font-weight: 800;
  color: var(--theme-text-highlight, #ffda6b);
}

/* ===========================
   Body Layout
   =========================== */
.pinduo-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

/* ===========================
   Wheel Card (left col)
   =========================== */
.wheel-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 20px 16px;
  backdrop-filter: blur(6px);
}

.wheel-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wheel-outer-box {
  position: relative;
  width: 340px;
  height: 340px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.character-backdrop {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  object-fit: contain;
  top: 50.3%;
  left: 49.5%;
  transform: translate(-49%, -49%);
}

.win-light-bg {
  position: absolute;
  width: 110%;
  height: 110%;
  z-index: 4;
  top: 45%;
  left: 47.5%;
  transform: translate(-48%, -48%);
  pointer-events: none;
  animation: pulseGlow 1.2s ease-in-out infinite;
}

@keyframes pulseGlow {
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; transform: translate(-48%, -48%) scale(1.03); }
}

.wheel-border {
  position: absolute;
  width: 83%;
  height: 83%;
  z-index: 5;
  pointer-events: none;
  top: 45%;
  left: 47.5%;
  transform: translate(-48%, -48%);
}

.wheel-inner {
  position: absolute;
  width: 80%;
  height: 80%;
  z-index: 3;
  transition: transform 4s cubic-bezier(0.15, 0, 0.15, 1);
  display: flex;
  justify-content: center;
  align-items: center;
  top: 46%;
  left: 48.5%;
}

.wheel-plate {
  width: 100%;
  height: 100%;
}

.prize-slot {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 6%;
}

.prize-text {
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
  margin-bottom: 6px;
  white-space: nowrap;
}

.p-icon {
  width: 32px;
  height: auto;
  margin-top: 22px;
}

.spin-center {
  position: absolute;
  top: 42%;
  left: 48%;
  transform: translate(-49%, -49%);
  width: 90px;
  height: 90px;
  z-index: 6;
  cursor: pointer;
  transition: transform 0.15s;
}
.spin-center:hover {
  transform: translate(-49%, -49%) scale(1.07);
}
.spin-center:active {
  transform: translate(-49%, -49%) scale(0.95);
}

.spin-btn-img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.spin-text-overlay {
  position: absolute;
  top: 75%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-size: 0.88rem;
  font-weight: 900;
  color: #5a3c00;
  line-height: 1.1;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Invite Row */
.invite-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.invite-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #bbb;
  text-align: center;
}

.invite-info svg {
  flex-shrink: 0;
  color: var(--theme-text-highlight, #ffda6b);
}

.invite-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 50px;
  background: var(--theme-btn-gradient, linear-gradient(to bottom, #f9e19d 0%, #d89f3c 50%, #b37e2a 100%));
  border-radius: 99px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  color: #4a2e00;
  font-weight: 900;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4), inset 0 2px 6px rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.invite-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.5), inset 0 2px 6px rgba(255, 255, 255, 0.4);
}
.invite-btn:active {
  transform: scale(0.97);
}

/* ===========================
   Balance Card
   =========================== */
.balance-card {
  background: var(--theme-bg-card, rgba(34, 51, 34, 0.45));
  border: 1px solid var(--theme-border-card, #4ca32e);
  border-radius: 20px;
  padding: 24px 20px;
  position: relative;
  overflow: hidden;
}

.balance-card::before {
  content: '';
  position: absolute;
  top: -40px;
  right: -40px;
  width: 120px;
  height: 120px;
  background: var(--theme-primary, #4ca32e);
  border-radius: 50%;
  opacity: 0.08;
}

.bc-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #999;
  margin-bottom: 6px;
}

.bc-amount {
  font-size: 2.6rem;
  font-weight: 900;
  color: var(--theme-text-highlight, #ffda6b);
  line-height: 1;
  margin-bottom: 22px;
  letter-spacing: -1px;
}

.progress-section {
  margin-bottom: 20px;
}

.progress-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.8rem;
  color: #aaa;
}

.progress-pct {
  color: var(--theme-text-highlight, #ffda6b);
  font-weight: 700;
}

.progress-bar-bg {
  width: 100%;
  height: 10px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 99px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--theme-progress-fill, linear-gradient(to right, #ffda6b, #4ca32e));
  border-radius: 99px;
  position: relative;
  transition: width 0.5s ease;
}

.progress-glow {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--theme-accent, #ffda6b);
  box-shadow: 0 0 10px 3px rgba(255, 218, 107, 0.6);
}

.progress-goal {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 0.8rem;
}

.goal-note {
  color: #aaa;
}

.goal-remain {
  color: #ff9944;
}

.goal-remain strong {
  color: #ffb65c;
}

.claim-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  background: var(--theme-primary, #4ca32e);
  border: none;
  border-radius: 12px;
  color: #fff;
  font-weight: 800;
  font-size: 1rem;
  box-shadow: 0 4px 14px rgba(76, 163, 46, 0.4);
  cursor: pointer;
  transition: all 0.2s;
}
.claim-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(76, 163, 46, 0.5);
}
.claim-btn:active:not(:disabled) {
  transform: translateY(0);
}
.claim-btn:disabled {
  background: #333;
  box-shadow: none;
  opacity: 0.45;
  cursor: not-allowed;
}

/* ===========================
   Records Card
   =========================== */
.records-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  overflow: hidden;
}

.record-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px 14px;
  font-size: 1rem;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.record-icon {
  font-size: 1.2rem;
}

.record-table {
  padding: 0 20px;
}

.table-header {
  display: grid;
  grid-template-columns: 1.2fr 1.5fr 1fr;
  padding: 10px 0;
  font-size: 0.78rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.table-header span:last-child {
  text-align: right;
}

.table-row {
  display: grid;
  grid-template-columns: 1.2fr 1.5fr 1fr;
  padding: 12px 0;
  font-size: 0.88rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  align-items: center;
  transition: background 0.15s;
}
.table-row:hover {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.row-highlight {
  background: rgba(76, 163, 46, 0.07);
}

.user-name {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-weight: 600;
}

.user-dot {
  width: 7px;
  height: 7px;
  background: var(--theme-primary, #4ca32e);
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 6px rgba(76, 163, 46, 0.7);
}

.reg-time {
  color: #666;
  font-size: 0.82rem;
}

.reward-text {
  color: var(--theme-text-highlight, #ffda6b);
  font-weight: 800;
  text-align: right;
}

.record-footer {
  padding: 14px 20px;
  display: flex;
  justify-content: center;
}

.btn-load-more-rec {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #888;
  padding: 8px 24px;
  border-radius: 8px;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-load-more-rec:hover {
  border-color: var(--theme-primary, #4ca32e);
  color: var(--theme-primary, #4ca32e);
  background: rgba(76, 163, 46, 0.07);
}

/* ===========================
   Steps Card
   =========================== */
.steps-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 20px;
}

.steps-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #ccc;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.step-num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--theme-primary, #4ca32e);
  color: #fff;
  font-size: 0.82rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(76, 163, 46, 0.4);
}

.step-text {
  font-size: 0.85rem;
  color: #bbb;
  padding-top: 5px;
  line-height: 1.4;
}

.step-hl {
  color: var(--theme-text-highlight, #ffda6b);
  font-weight: 700;
}

.step-connector {
  width: 2px;
  height: 16px;
  background: rgba(76, 163, 46, 0.3);
  margin-left: 13px;
}

/* ===========================
   PC Responsive (>= 900px)
   =========================== */
@media (min-width: 900px) {
  .title-badge {
    display: inline-flex;
    align-items: center;
  }

  .header-countdown {
    display: flex;
  }

  .pinduo-body {
    flex-direction: row;
    align-items: flex-start;
    gap: 24px;
    padding: 24px 32px;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
  }

  .col-left {
    flex: 0 0 420px;
    position: sticky;
    top: 84px; /* header + ticker */
  }

  .col-right {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }

  .wheel-outer-box {
    width: 380px;
    height: 380px;
  }

  .invite-btn {
    max-width: 360px;
  }
}

@media (min-width: 1200px) {
  .wheel-outer-box {
    width: 400px;
    height: 400px;
  }

  .bc-amount {
    font-size: 3rem;
  }
}

/* Mobile fallback */
@media (max-width: 899px) {
  .col-left, .col-right {
    width: 100%;
  }

  .col-right {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .wheel-outer-box {
    width: 300px;
    height: 300px;
  }
}
</style>
